<template>
  <div class="edit-form-panel" v-if="visible">
    <div class="form-header">
      <span class="title">{{ formData.id ? '编辑信息' : '新增信息' }}</span>
      <a-icon type="close" class="close-icon" @click="close" />
    </div>
    <div class="form-content">
      <a-form-model ref="form" :model="formData" :rules="rules" layout="vertical">
        <a-form-model-item v-for="item in formFields" :key="item.id" :label="item.name" :prop="item.id"
          class="custom-form-item">
          <a-input v-if="!item.type || item.type === 'input'" v-model="formData[item.id]" placeholder="请输入"
            :disabled="isFieldDisabled(item.id)" />
          <a-input-number v-else-if="item.type === 'number'" v-model="formData[item.id]" placeholder="请输入数字"
            style="width: 100%" :disabled="isFieldDisabled(item.id)" />
          <a-select v-else-if="item.type === 'select'" :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model="formData[item.id]" placeholder="请选择" :disabled="isFieldDisabled(item.id)">
            <a-select-option v-for="opt in item.options" :key="opt" :value="opt">{{ opt }}</a-select-option>
          </a-select>
        </a-form-model-item>
      </a-form-model>
    </div>
    <div class="form-footer">
      <div class="move-point-btn" v-if="formData.id && currentType === 'gis_pipe_network'">
        <a-button size="small" :type="isMovingPoint ? 'danger' : 'default'" @click="toggleMovePoint">
          {{ isMovingPoint ? '取消移动' : '移动点' }}
        </a-button>
      </div>
      <a-button size="small" @click="close">取消</a-button>
      <a-button type="primary" size="small" @click="handleSave" :loading="saving">保存</a-button>
    </div>
  </div>
</template>

<script>
import formConfig from './formConfig.js'
import request from '@/utils/request'

export default {
  name: 'EditForm',
  data () {
    return {
      visible: false,
      saving: false,
      formData: {},
      formFields: [],
      currentType: '',
      originalData: null,
      isMovingPoint: false
    }
  },
  computed: {
    rules () {
      const rules = {};
      this.formFields.forEach(item => {
        if (item.required) {
          rules[item.id] = [
            { required: true, message: `${item.name}不能为空`, trigger: item.type === 'select' ? 'change' : 'blur' }
          ];
        }
      });
      return rules;
    }
  },
  methods: {
    show (data, type) {
      this.originalData = data;
      this.currentType = type;
      this.formData = { ...data };
      this.isMovingPoint = false;
      this.$emit('stop-move-point');

      // 优先从自定义 formConfig 获取配置
      let config = formConfig[type];

      // 如果没有精确匹配，尝试模糊匹配通用配置
      if (!config) {
        if (type.startsWith('gis_pipe_network')) {
          config = formConfig.gis_pipe_network;
        } else if (type.startsWith('gis_pipe_line')) {
          config = formConfig.gis_pipe_line;
        }
      }

      if (config) {
        this.formFields = config;
      } else {
        this.formFields = [];
        console.warn(`未找到类型 ${type} 的表单配置`);
      }
      this.visible = true;
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    handleSave () {
      this.$refs.form.validate(valid => {
        if (valid) {
          const isLine = this.currentType.startsWith('gis_pipe_line');
          const isAdd = !this.formData.id;

          // 新增模式下的校验逻辑
          if (isAdd) {
            if (isLine) {
              // 管线模式：校验起止点信息
              const hasStart = this.formData.startClassNo || this.formData.startGeophysicalPointNumber;
              const hasEnd = this.formData.endClassNo || this.formData.endGeophysicalPointNumber;
              if (!hasStart || !hasEnd) {
                this.$message.warning('请先在地图上依次点击两个点以建立连线');
                return;
              }
            } else {
              // 点位模式：校验坐标信息
              if (!this.formData.point_x || !this.formData.point_y) {
                this.$message.warning('请先在地图上点击选择点位位置');
                return;
              }
            }
          }

          this.saving = true;
          this.$emit('loading', true, '正在保存编辑...');

          let url = '';
          if (isLine) {
            url = isAdd ? '/pipe-network/gis/pipe-lines/add/line' : '/pipe-network/gis/pipe-lines/edit/line';
          } else {
            url = isAdd ? '/pipe-network/gis/pipe-networks/add/point' : '/pipe-network/gis/pipe-networks/edit/point';
          }

          // 过滤参数，只保留配置中存在的字段
          const submitData = {};
          if (!isAdd) {
            submitData.id = this.formData.id; // 只有编辑模式需要包含 ID
          }

          this.formFields.forEach(field => {
            if (this.formData[field.id] !== undefined) {
              submitData[field.id] = this.formData[field.id];
            }
          });

          request({
            url: url,
            method: 'post',
            data: submitData
          }).then(res => {
            if (res.success || res.code === 200 || res.code === 0) {
              this.$message.success('保存成功');
              this.$emit('save', this.formData, res.data, this.currentType);
              this.close();
            } else {
              this.$message.error(res.message || '保存失败');
            }
          }).catch(err => {
            console.error(err);
            this.$message.error(err.data.message);
          }).finally(() => {
            this.saving = false;
            this.$emit('loading', false);
          });
        } else {
          this.$message.error('请完善表单信息');
          return false;
        }
      });
    },
    close () {
      this.visible = false;
      this.isMovingPoint = false;
      this.$emit('close');
    },
    toggleMovePoint () {
      this.isMovingPoint = !this.isMovingPoint;
      if (this.isMovingPoint) {
        this.$message.info('请在地图上点击新位置');
        this.$emit('start-move-point');
      } else {
        this.$emit('stop-move-point');
      }
    },
    /**
     * 判断字段是否禁用 (新增模式下特定字段不可手动填写)
     */
    isFieldDisabled (fieldId) {
      // 仅在新增模式下限制
      if (this.formData.id) return false;

      const disabledFields = [
        'startClassNo',
        'endClassNo',
        'startGeophysicalPointNumber',
        'endGeophysicalPointNumber'
      ];

      return disabledFields.includes(fieldId);
    },
    /**
     * 更新坐标信息 (通常由外部地图点击触发)
     */
    updateCoordinates (coords) {
      if (Array.isArray(coords) && coords.length >= 2) {
        if (this.isMovingPoint) {
          const _this = this;
          this.$confirm({
            title: '确认移动点',
            content: `确定要将点位移动到坐标 [${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}] 吗？`,
            okText: '确定',
            cancelText: '取消',
            onOk () {
              _this.$set(_this.formData, 'point_x', coords[0]);
              _this.$set(_this.formData, 'point_y', coords[1]);
              _this.isMovingPoint = false;
              _this.$emit('stop-move-point');
              _this.handleSave();
            },
            onCancel () {
              // 取消时不执行操作
            }
          });
        } else {
          this.$set(this.formData, 'point_x', coords[0]);
          this.$set(this.formData, 'point_y', coords[1]);
        }
      }
    },
    /**
     * 批量更新表单字段
     */
    updateFields (fields) {
      Object.keys(fields).forEach(key => {
        this.$set(this.formData, key, fields[key]);
      });
    },
    toCamelCase (str) {
      return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
    }
  }
}
</script>

<style lang="less" scoped>
.edit-form-panel {
  position: absolute;
  left: 20px;
  top: 20px;
  bottom: 100px; // 为底部工具栏留出空间
  width: 320px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1002;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e8e8e8;
  overflow: hidden;

  .form-header {
    padding: 0 16px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #ffffff;
    height: 48px;

    .title {
      font-weight: 600;
      color: #262626;
      font-size: 16px;
      display: flex;
      align-items: center;

      &::before {
        content: '';
        display: inline-block;
        width: 4px;
        height: 16px;
        background: #1890ff;
        margin-right: 10px;
        border-radius: 2px;
      }
    }

    .close-icon {
      cursor: pointer;
      color: #8c8c8c;
      font-size: 16px;
      padding: 8px;
      transition: all 0.3s;
      border-radius: 4px;

      &:hover {
        background: #fff1f0;
        transform: rotate(90deg);
        color: #ff4d4f;
      }
    }
  }

  .form-content {
    flex: 1;
    padding: 20px 20px 0 20px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background: #e8e8e8;
      border-radius: 3px;
    }

    .custom-form-item {
      margin-bottom: 5px;

      /deep/ .ant-form-item-label {
        padding-bottom: 6px;
        line-height: 1.4;

        label {
          font-size: 13px;
          color: #595959;
          font-weight: 500;

          &::after {
            content: ''; // 去掉默认冒号
          }
        }
      }

      /deep/ .ant-input,
      /deep/ .ant-input-number,
      /deep/ .ant-select-selection {
        border-radius: 4px;
        border-color: #d9d9d9;

        &:hover {
          border-color: #40a9ff;
        }

        &:focus {
          border-color: #40a9ff;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        }
      }

      /deep/ .ant-input {
        padding: 6px 11px;
      }
    }
  }

  .form-footer {
    padding: 16px 20px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    background: #ffffff;

    .ant-btn {
      min-width: 80px;
      height: 34px;
      border-radius: 4px;
    }

    .move-point-btn {
      margin-right: auto;
    }
  }
}
</style>
